import { cn } from "@/lib/utils";
import type { ReactNode } from "react";
import React from "react";

/**
 * We create a minimal interface for the `field` prop that only includes
 * the layout components we need. This is stable and avoids the complex generics.
 */
interface FormMessageProps {
  children?: ReactNode;
  className?: string;
}

interface FieldWithLayout {
  FormItem: React.ComponentType<{ children: ReactNode }>;
  FormLabel: React.ComponentType<{ children: ReactNode; className?: string }>;
  FormControl: React.ComponentType<{ children: ReactNode }>;
  FormMessage: React.ComponentType<FormMessageProps>;
}

interface FormFieldWrapperProps {
  field: FieldWithLayout;
  label?: ReactNode;
  labelClassName?: string;
  children: ReactNode;
  formMessage?: ReactNode;
}

/**
 * This component provides the boilerplate wrapper for a form field.
 * It's designed to be used inside the render prop of `<form.AppField>`.
 */
export function FormFieldWrapper({
  field,
  label,
  labelClassName,
  children,
  formMessage,
}: FormFieldWrapperProps) {
  const isDisabled = React.useMemo(() => {
    return React.Children.toArray(children).some((child) => {
      if (React.isValidElement(child)) {
        // Type assertion for the props
        const props = child.props as { disabled?: boolean };
        return props.disabled === true;
      }
      return false;
    });
  }, [children]);

  return (
    <field.FormItem>
      {label && (
        <field.FormLabel
          className={cn(
            "font-bold",
            labelClassName,
            isDisabled && "opacity-50 text-muted-foreground",
          )}
        >
          {label}
        </field.FormLabel>
      )}
      <field.FormControl>{children}</field.FormControl>
      <field.FormMessage className="">{formMessage}</field.FormMessage>
    </field.FormItem>
  );
}
